import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, limit, orderBy, query } from "firebase/firestore";
import { db } from "../firebase";
import Window from "../components/Window";
import Icon, { IconTile } from "../components/Icon";
import { ROOMS, Thread } from "../rooms";
import { useBlocks } from "../blocks";
import "../styles.css";

// The room list, each with its most recent conversation.
export default function Community() {
  const [recent, setRecent] = useState<Thread[] | null>(null);
  const { ids: blocked } = useBlocks();

  useEffect(() => {
    getDocs(query(collection(db, "roomThreads"), orderBy("lastReplyAt", "desc"), limit(100)))
      .then((snap) => setRecent(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Thread, "id">) }))))
      .catch((err) => {
        console.error(err);
        setRecent([]);
      });
  }, []);

  const latestIn = (room: string) =>
    (recent || []).find((t) => t.room === room && !t.removed && !(t.uid && blocked.has(t.uid)));

  return (
    <>
      <Window title="community_rooms.htm">
        <h1 className="window-heading flex items-center gap-3">
          <IconTile name="community" tone="green" size={44} /> Community Rooms
        </h1>
        <p className="window-sub !mb-0">
          Pick a room and pull up a chair. Start a conversation, or answer one.
          Be gentle, use content warnings, and remember the{" "}
          <Link to="/rules">community guidelines</Link>.
        </p>
      </Window>

      <div className="grid gap-4 sm:grid-cols-2">
        {ROOMS.map((room) => {
          const latest = latestIn(room.id);
          return (
            <Link key={room.id} to={`/community/${room.id}`} className="tile bg-paper">
              <h2 className="tile-title !mt-0">{room.title}</h2>
              <p className="text-sm">{room.description}</p>
              <p className="mt-2 text-xs text-ink-soft">
                {recent === null ? (
                  "..."
                ) : latest ? (
                  <>
                    <Icon name="mail" size={12} /> Latest: {latest.cw ? `CW: ${latest.cw}` : latest.title}
                  </>
                ) : (
                  "No conversations yet. Start the first one!"
                )}
              </p>
            </Link>
          );
        })}
        <Link to="/post" className="tile bg-paper">
          <h2 className="tile-title !mt-0">
            <Icon name="mail" size={16} /> The PenPal Post
          </h2>
          <p className="text-sm">
            Our little mail service and journal: news from the Post Office, plus
            poems, essays, stories, and history from members. Drop your own
            writing in the mailbox.
          </p>
        </Link>
        <Link to="/circles" className="tile bg-paper">
          <h2 className="tile-title !mt-0">
            <Icon name="lock" size={16} /> Private Circles
          </h2>
          <p className="text-sm">
            Small, members-only spaces for folks navigating similar things:
            sobriety, transitions, disability, QTPOC connection & more. Ask to join
            one, or start your own and be its quartermaster.
          </p>
        </Link>
        <Link to="/get-involved" className="tile bg-paper">
          <h2 className="tile-title !mt-0">
            <Icon name="heart" size={16} /> Get involved
          </h2>
          <p className="text-sm">
            All the ways to take part, from writing for the PenPal Post to
            looking after a circle or helping moderate.
          </p>
        </Link>
      </div>
    </>
  );
}
